import {
  createRootRoute, createRoute, createRouter,
  RouterProvider, redirect, Outlet,
} from '@tanstack/react-router';
import { RootLayout } from './components/Layout.tsx';
import { Login } from './pages/Login.tsx';
import { Dashboard } from './pages/Dashboard.tsx';
import { NotFound } from './pages/NotFound.tsx';
import { Vendors } from './pages/Vendors.tsx';
import { VendorEdit } from './pages/VendorEdit.tsx';
import { Requests } from './pages/Requests.tsx';
import { RequestNew } from './pages/RequestNew.tsx';
import { RequestDetail } from './pages/RequestDetail.tsx';
import { Gsrs } from './pages/Gsrs.tsx';
import { GsrNew } from './pages/GsrNew.tsx';
import { GsrDetail } from './pages/GsrDetail.tsx';
import { PurchaseRequestDetail } from './pages/PurchaseRequestDetail.tsx';
import { OrgSettings } from './pages/OrgSettings.tsx';
import { Payments } from './pages/Payments.tsx';
import { PublicQuote } from './pages/PublicQuote.tsx';
import { InvoiceConfirm } from './pages/InvoiceConfirm.tsx';
import { InvoiceSlip } from './pages/InvoiceSlip.tsx';
import { SsoHandoff } from './pages/SsoHandoff.tsx';
import { VendorLogin } from './pages/vendor/VendorLogin.tsx';
import { VendorLayout } from './pages/vendor/VendorLayout.tsx';
import { VendorQuotations } from './pages/vendor/VendorQuotations.tsx';
import { VendorOpenRequests } from './pages/vendor/VendorOpenRequests.tsx';
import { VendorAwards } from './pages/vendor/VendorAwards.tsx';
import { VendorRequestDetail } from './pages/vendor/VendorRequestDetail.tsx';
import { useAuthStore } from './store/auth.ts';
import { useVendorAuthStore } from './store/vendorAuth.ts';

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
});

const requireAuth = (): void => {
  if (!useAuthStore.getState().accessToken) throw redirect({ to: '/login' });
};

const requireVendorAuth = (): void => {
  if (!useVendorAuthStore.getState().accessToken) throw redirect({ to: '/vendor/login' });
};

// Public routes
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: Login,
});

// Authed routes
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: requireAuth,
  component: Dashboard,
});

// /dashboard is a common bookmark — home page lives at '/'. Redirect.
const dashboardRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  beforeLoad: () => { throw redirect({ to: '/' }); },
});

// Vendor routes
const vendorsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vendors',
  beforeLoad: requireAuth,
  component: Vendors,
});

const vendorEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vendors/$vendorId',
  beforeLoad: requireAuth,
  component: VendorEdit,
});

// Request routes
const requestsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/requests',
  beforeLoad: requireAuth,
  component: Requests,
});

const requestNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/requests/new',
  beforeLoad: requireAuth,
  component: RequestNew,
});

const requestDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/requests/$requestId',
  beforeLoad: requireAuth,
  component: RequestDetail,
});

// GSR routes
const gsrsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/gsr',
  beforeLoad: requireAuth,
  component: Gsrs,
});

const gsrNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/gsr/new',
  beforeLoad: requireAuth,
  component: GsrNew,
});

const gsrDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/gsr/$gsrId',
  beforeLoad: requireAuth,
  component: GsrDetail,
});

const purchaseRequestDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/purchase-requests/$prId',
  beforeLoad: requireAuth,
  component: PurchaseRequestDetail,
});

const orgSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/org-settings',
  beforeLoad: requireAuth,
  component: OrgSettings,
});

const paymentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/payments',
  beforeLoad: requireAuth,
  component: Payments,
});

// Public vendor quote route — no auth guard, no sidebar/header.
// Uses a layout route that renders just <Outlet /> (bypasses RootLayout shell).
const publicLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'public-layout',
  component: () => <Outlet />,
});

const publicQuoteRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/q/$token',
  component: PublicQuote,
});

// Public vendor invoice-confirm route — no auth guard, no sidebar/header.
const invoiceConfirmRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/invoice-confirm/$token',
  component: InvoiceConfirm,
});

// Public vendor transfer-slip route — no auth guard, no sidebar/header.
const invoiceSlipRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/invoice-slip/$token',
  component: InvoiceSlip,
});

// SSO handoff — public route, no auth guard, no app shell.
const ssoHandoffRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/sso/handoff',
  component: SsoHandoff,
});

// ── Vendor portal (self-contained shell, separate vendor auth) ────────────────
const vendorLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vendor/login',
  component: VendorLogin,
});

const vendorLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vendor',
  beforeLoad: requireVendorAuth,
  component: VendorLayout,
});

const vendorQuotationsRoute = createRoute({
  getParentRoute: () => vendorLayoutRoute,
  path: '/',
  component: VendorQuotations,
});

const vendorOpenRoute = createRoute({
  getParentRoute: () => vendorLayoutRoute,
  path: 'open',
  component: VendorOpenRequests,
});

const vendorAwardsRoute = createRoute({
  getParentRoute: () => vendorLayoutRoute,
  path: 'awards',
  component: VendorAwards,
});

const vendorRequestDetailRoute = createRoute({
  getParentRoute: () => vendorLayoutRoute,
  path: '/requests/$requestId',
  component: VendorRequestDetail,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  indexRoute,
  dashboardRedirectRoute,
  vendorsRoute,
  vendorEditRoute,
  requestsRoute,
  requestNewRoute,
  requestDetailRoute,
  gsrsRoute,
  gsrNewRoute,
  gsrDetailRoute,
  purchaseRequestDetailRoute,
  orgSettingsRoute,
  paymentsRoute,
  publicLayoutRoute.addChildren([publicQuoteRoute, invoiceConfirmRoute, invoiceSlipRoute, ssoHandoffRoute]),
  vendorLoginRoute,
  vendorLayoutRoute.addChildren([vendorQuotationsRoute, vendorOpenRoute, vendorAwardsRoute, vendorRequestDetailRoute]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register { router: typeof router }
}

export function AppRouter(): JSX.Element { return <RouterProvider router={router} />; }
